import {
  Dispatch,
  StoreEnhancer,
  StoreEnhancerStoreCreator,
  UnknownAction,
} from '@reduxjs/toolkit';
import { RootInitialState } from 'core/common/store/rootReducer';
import { evalCondition } from 'core/common/utils/evalCondition';
import { FeatureRawResult } from 'core/feature-flags/entities';
import { FeatureFlags } from 'core/feature-flags/services';
import { getFeatures, saveFeature } from 'core/feature-flags/store';
import { getBaseConditionAttributes } from 'core/funnel/store/baseConditionAttributes';
import { getOffersEntries } from '../selectors';

const DEFAULT_RESULT_SOURCES = new Set<FeatureRawResult['source']>([
  'defaultValue',
  'unknownFeature',
]);

export const offersFeatureObserver = (featureFlags: FeatureFlags): StoreEnhancer =>
  ((createStore: StoreEnhancerStoreCreator) => (reducer, preloadedState) => {
    const store = createStore(reducer, preloadedState);
    const dispatch = store.dispatch as Dispatch<UnknownAction>;

    const seedReachableFeature = () => {
      const state = store.getState() as unknown as RootInitialState;
      const attributes = getBaseConditionAttributes(state);

      const reachableEntry = getOffersEntries(state).find(({ condition, feature }) => {
        const isConditionMatched = !condition || !![attributes].find(evalCondition(condition));

        return !!feature && isConditionMatched;
      });

      const [featureName] = Object.keys(reachableEntry?.feature ?? {});

      if (!featureName) {
        return;
      }

      const storedValue = getFeatures(state)[featureName];

      const { value, source } = featureFlags.getFeatureRawResult<string>(featureName);

      const isValueChanged = !!value && value !== storedValue;
      const isRuleMatched = !DEFAULT_RESULT_SOURCES.has(source);
      const canOverwrite = !storedValue || isRuleMatched;
      const shouldSave = isValueChanged && canOverwrite;

      if (shouldSave) {
        dispatch(saveFeature({ name: featureName, value }));
      }
    };

    seedReachableFeature();
    store.subscribe(seedReachableFeature);

    return store;
  }) as StoreEnhancer;
